import Text from "@/components/Text";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
} from "@mui/material";
import Spacer from "@/components/Spacer";
import TextField from "@/components/TextField";
import Button from "@/components/Button";
import CpfInput from "@/components/CpfInput";
import CloseIcon from "@mui/icons-material/Close";
import Theme, { dialogBackdropSx } from "@/utils/theme";
import { useEffect, useState } from "react";
import { useUser } from "@/context/UserContext";
import { needsCompleteRegistration } from "@/utils/registrationProfile";
import { useCompleteRegistrationForm } from "@/hooks/useCompleteRegistrationForm";
import { ProfileImageInput } from "@/pages/Profile/components/ProfileImageInput";
import { UserImage } from "@/pages/Profile/styles";
import avatar from "/assets/avatar.png";

/**
 * Prompts for profile photo, CPF and profession data when still missing after phone validation.
 */
export function CompleteRegistrationModal() {
  const { userInfo, isLoadingUser } = useUser();
  const [dismissed, setDismissed] = useState(false);
  const {
    imageUrl,
    setImageUrl,
    cpf,
    setCpf,
    cpfError,
    filteredOccupations,
    selectedOccupationId,
    setSelectedOccupationId,
    timeOfProfession,
    setTimeOfProfession,
    isValid,
    onSubmit,
    timeOfProfessionOptions,
  } = useCompleteRegistrationForm();

  const registrationRequired = needsCompleteRegistration(userInfo);

  useEffect(() => {
    if (!registrationRequired) {
      setDismissed(false);
    }
  }, [registrationRequired]);

  const open =
    !isLoadingUser && !!userInfo?.id && registrationRequired && !dismissed;

  const handleDialogClose = () => {
    setDismissed(true);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{ sx: dialogBackdropSx }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: 0,
          mx: 0,
          mb: 2,
          px: { xs: 2, sm: 2.5 },
          py: { xs: 2.25, sm: 2.75 },
          fontFamily: "Montserrat",
          fontWeight: 700,
          bgcolor: Theme.colors.brand.primary,
          color: Theme.colors.text.primary,
        }}
      >
        Complete seu cadastro
        <IconButton
          aria-label="Fechar"
          onClick={handleDialogClose}
          size="small"
          sx={{ color: Theme.colors.text.primary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 0 }}>
        <Text fontSize="0.95rem" color={Theme.colors.text.primary}>
          Para acessar a plataforma, envie sua foto, informe seu CPF, principal atividade de obra e tempo de profissão.
          Você poderá alterar esses dados em Meu Perfil quando permitido.
        </Text>
        <Text fontSize="0.8rem" mt="0.75rem" color={Theme.colors.feedback.warning}>
          Você pode fechar e concluir depois. Alguns recursos podem ficar limitados até finalizar o cadastro.
        </Text>

        <Spacer height="1.25rem" />

        <Text fontSize="0.875rem" fontWeight={600} color={Theme.colors.text.primary}>
          Foto do perfil*
        </Text>
        <Spacer height="0.5rem" />
        <Box display="flex" justifyContent="center" width="100%">
          <Box position="relative" width="fit-content" height="fit-content">
            <ProfileImageInput setImageUrl={setImageUrl} disabled={false} />
            <UserImage src={imageUrl || avatar} alt="" />
          </Box>
        </Box>

        <Spacer height="1.25rem" />

        <CpfInput
          label="CPF*"
          value={cpf}
          setValue={setCpf}
          error={!!cpfError}
          errorText={cpfError}
          ContainerProps={{ width: "100%" }}
        />

        <Spacer height="1rem" />

        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
          <Box flex={1} minWidth={0}>
            <TextField
              label="Principal atividade de obra*"
              value={selectedOccupationId}
              setValue={setSelectedOccupationId}
              select
              ContainerProps={{ width: "100%" }}
              sx={{
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {filteredOccupations.map((occupation) => (
                <MenuItem key={occupation.id} value={occupation.id.toString()}>
                  {occupation.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box flex={1} minWidth={0}>
            <TextField
              label="Tempo de profissão*"
              value={timeOfProfession}
              setValue={setTimeOfProfession}
              select
              ContainerProps={{ width: "100%" }}
            >
              {timeOfProfessionOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 },
          pb: 2,
          pt: 2.5,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <Button size="large" fullWidth onClick={() => void onSubmit()} disabled={!isValid}>
          Concluir cadastro
        </Button>
      </DialogActions>
    </Dialog>
  );
}
